/**
 * =============================================================================
 * SEMANTIC DESIGN TOKENS — ADR-025 B-6 (BL-ADMIN-WORKBENCH-IA-007)
 * =============================================================================
 *
 * The single canonical home for every color, type-scale, and spacing value the
 * admin UI previously carried as drifting inline literals (ADR-025 §6.2).
 * Naming is DTCG-compatible (`group.concept.property` paths — Design Tokens
 * Community Group), so a vendor-neutral token exchange (e.g. Polaris on a
 * future Shopify host) is possible later at zero present cost (OD-17).
 *
 * VALUE POLICY (owner-ratified, 2026-07-19 — docs/reviews/adr-025/b6-design-gate/
 * final-decisions.md): Q1 = A′. Text roles pass WCAG 2.2 AA (4.5:1) on their
 * ACTUAL backgrounds; meaningful accents pass 3:1; drifting near-duplicates
 * consolidate only where the delta is imperceptible; noticeable values are
 * preserved unless a binding requirement compels change; kept-verbatim pinned
 * surfaces (QualityBadge, Analytics chart palette) are tracked in the
 * KNOWN_FAILURES ledger (contrast.test.ts) with BACKLOG rows.
 *
 * Where a value coincides with @bigcommerce/big-design-theme, the theme name is
 * noted in a comment (the theme package stays a transitive dependency — tokens
 * are self-contained by design). BigDesign *named* props (padding="medium",
 * backgroundColor="secondary10", marginBottom="small", Badge variants) are
 * already tokens and are NOT migrated to this module.
 *
 * Contrast evidence for every text/accent pairing: contrast.test.ts (computed
 * WCAG relative luminance — the axe `color-contrast` rule cannot run in jsdom).
 * Drift guards: styleDrift.test.ts (TypeScript-AST scanners; sourceGuards.ts).
 * =============================================================================
 */

/** BigDesign `primary` — brand blue (focus rings, chart active bars, metric text). */
const BRAND_BLUE = '#3c64f4';

export const tokens = {
  color: {
    status: {
      /**
       * House "attention / pending work" hue. Value adjusted #b35c00 → #ad5900
       * in B-6 (final-decisions.md Q8): an imperceptible ~2% darkening (ΔRGB ≤ 6)
       * so ONE canonical value passes AA on every actual background —
       * 4.67:1 on secondary10 (#F6F7FC, the queue stats strip) and 4.99:1 on
       * white. `marker` is the aria-hidden 8px unsaved-changes dot background
       * (AdminShell) — decorative; state is carried by the button's aria-label.
       */
      attention: { text: '#ad5900', marker: '#ad5900' },
      success: {
        /** 12px upload-status text — TEXT role (5.35:1 on white). Never map to `accent`. */
        text: '#207955',
        /** = BigDesign `success` (3.00:1 on white — meets 1.4.11; margin is thin: 3.002). */
        accent: '#2aab3f',
        /** = BigDesign `success10` (consolidates #ecfdf5 / #e6f4ea / #f0fdf4). */
        surface: '#eefcf0',
      },
      warning: {
        /**
         * KEPT VERBATIM (Q1 = A′): every surviving use is decorative-redundant
         * (RFI notice border beside tinted bg + text; solution star whose signal
         * is the star's fill; toast accent bar beside icon + message), so WCAG
         * 1.4.11 does not bind (2.15:1 on white, documented). Consolidates the
         * old toast amber #fbbc04 into this value.
         */
        accent: '#f59e0b',
        /** = BigDesign `warning10` (consolidates #fffbeb / #fff8e1 / #fef7e0). */
        surface: '#fff9e6',
      },
      danger: {
        /** = BigDesign `danger` — 4.54:1 on white, passes even as small text. */
        text: '#db3643',
        accent: '#db3643',
        /** = BigDesign `danger10` (consolidates toast #fce8e6). */
        surface: '#fff0f1',
      },
      info: {
        /** Selected-option highlight (RFI create form). */
        surface: '#eef2ff',
      },
    },
    border: {
      /** = BigDesign `secondary30` (consolidates #e5e7eb / #d1d5db / #ddd / #e4e7eb / #d9dce9). */
      default: '#d9dce9',
    },
    surface: {
      base: '#ffffff',
      /** = BigDesign `secondary10` (consolidates #f9fafb / #fafbfc / #f0f0f0). */
      subtle: '#f6f7fc',
      /** = BigDesign `secondary20` — skeleton/placeholder fills (consolidates the hand-rolled #e5e7eb blocks). */
      skeleton: '#eceef5',
    },
    text: {
      /** = BigDesign `secondary60` (consolidates #6b7280 / #374151) — 5.93:1 on white. */
      muted: '#5e637a',
    },
    interactive: {
      focusRing: BRAND_BLUE,
      focusRingInner: '#ffffff',
    },
    brand: { primary: BRAND_BLUE },
    chart: {
      /** AnalyticsDashboard status palette — KEPT VERBATIM (chart identity, not §6.3 status encoding). */
      status: {
        PENDING_REVIEW: '#F29D12',
        OPEN: '#4CAF50',
        IN_PROGRESS: '#2196F3',
        NEEDS_MORE_INFO: '#9C27B0',
        RESOLVED: '#8BC34A',
        CLOSED_DUPLICATE: '#607D8B',
        CLOSED_REJECTED: '#F44336',
      },
      bar: { active: BRAND_BLUE, track: '#e4e7eb' },
    },
    quality: {
      /** QualityBadge chips — KEPT VERBATIM (pinned hand-rolled surface); ledgered incl. the 0.7-opacity score compositing. */
      low: { bg: '#FFEAEA', text: '#D32F2F' },
      medium: { bg: '#FFF8E1', text: '#F57C00' },
      high: { bg: '#E8F5E9', text: '#2E7D32' },
    },
    toast: {
      success: { surface: '#eefcf0', accent: '#2aab3f' },
      error: { surface: '#fff0f1', accent: '#db3643' },
      warning: { surface: '#fff9e6', accent: '#f59e0b' },
    },
  },
  typography: {
    /**
     * rem-valued (Q6): pixel-identical at the BigDesign 16px root default,
     * scales with user font-size preferences (WCAG 1.4.4-adjacent), matches the
     * theme's own remCalc posture. px equivalents in comments.
     */
    scale: {
      /** 10px — COMPATIBILITY ONLY (legacy parity; do not adopt for new UI). */
      legacyMicro: { fontSize: '0.625rem' },
      /** 11px + tracking — nav group captions, attention micro-notes. */
      overline: { fontSize: '0.6875rem', letterSpacing: '0.05em' },
      /** 12px — metadata, counters, captions. */
      caption: { fontSize: '0.75rem' },
      /** 13px — compact body/cell text. */
      bodySmall: { fontSize: '0.8125rem' },
      /** 14px — emphasized row titles. */
      body: { fontSize: '0.875rem' },
      /** 32px — stat-tile metric numbers (unifies '32px' and '2rem'). */
      metric: { fontSize: '2rem' },
    },
  },
  dimension: {
    /**
     * Spacing tokens for RAW style-object literals only (gap, margin, padding);
     * exact-preserving — spacing is never consolidated (layout would shift).
     * BigDesign named spacing props remain the preferred mechanism.
     */
    space: {
      xxs: '2px',
      xs: '4px',
      xsm: '6px',
      sm: '8px',
      md: '12px',
      lg: '16px',
    },
    radius: {
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      pill: '50%',
    },
  },
} as const;

export type Tokens = typeof tokens;
